<template>
  <div class="checkbox-container">
    <input
      :id="label"
      v-model="checked"
      type="checkbox"
      :name="label"
      :value="label"
      :disabled="disabled"
      :class="{ fill: styling === 'fill', outline: styling === 'outline' }"
      @change="$emit('change', checked)"
    />
    <TextBase
      :text="label"
      :type="labelConfig?.type"
      :disabled="disabled"
      :style="{ display: 'flex', 'align-items': 'center' }"
    />
  </div>
</template>
<script lang="ts" setup>
import TextBase, { TextColor, TextType } from '@/design-system/atoms/TextBase.vue'
import { computed } from 'vue'

export type LabelConfig = {
  type?: TextType
  color?: TextColor
}

export type CheckboxProps = {
  label: string
  styling: 'fill' | 'outline'
  labelConfig?: LabelConfig
  color?: string
  initial?: boolean
  disabled?: boolean
  borderColor?: string
}

type Emits = {
  (e: 'change', checked: boolean): void
}

const props = defineProps<CheckboxProps>()
defineEmits<Emits>()

const checked = computed<boolean>(() => props.initial ?? false)
</script>

<style scoped lang="scss">
.checkbox-container {
  display: flex;
  align-items: center;

  input[type='checkbox'].fill {
    background-color: #fff;
    color: v-bind("color ?? 'currentColor'");
    border: 2px solid v-bind("borderColor ?? 'currentColor'");
  }
  input[type='checkbox'].outline {
    border: 2px solid v-bind("color ?? 'currentColor'");
  }
  input[type='checkbox'] {
    appearance: none;
    margin: 0;
    margin-right: 8px;
    font: inherit;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    transform: translateY(-1px);
    display: grid;
    cursor: pointer;
    place-content: center;
  }
  input[type='checkbox'].fill:checked {
    background-color: currentColor;
  }
  input[type='checkbox'].fill:checked::before {
    box-shadow: inset 8px 8px #fff;
  }
  input[type='checkbox'].outline:checked::before {
    box-shadow: inset 8px 8px v-bind("color ?? 'currentColor'");
  }
  input[type='checkbox']:checked::before {
    content: '';
    display: block;
    width: 11px;
    height: 11px;
    transform-origin: bottom left;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }
  input[type='checkbox']:focus {
    outline: none;
    box-shadow: 0 0 0 2px color-mix(in srgb, v-bind("color ?? 'currentColor'") 40%, transparent);
  }
  input[type='checkbox']:disabled {
    color: #767676;
    cursor: default;
  }
}
</style>
